import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  Home,
  RefreshCw,
  Search,
  Trash2,
  Printer,
  Receipt,
  FileText,
  FileSpreadsheet,
  ChevronsUpDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { PrintService } from "@/utils/PrintService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const toInputDate = (value: string) => value || "";

const getInvoiceDate = (date?: string) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";
  return format(parsed, "dd-MMM-yyyy");
};

const getReferenceNumber = (invoice: any, index: number) => {
  const raw = invoice.reference_no || invoice.invoice_no || invoice.bill_no || "";
  if (raw) return String(raw).padStart(5, "0");
  return String(index + 1).padStart(5, "0");
};

const exportCsv = (rows: any[]) => {
  const header = ["Sl.No", "Reference No.", "Date", "Party Type", "Org/Cust"];
  const body = rows.map((row, index) => [
    index + 1,
    getReferenceNumber(row, index),
    getInvoiceDate(row.date),
    row.party_type || "",
    row.party_name || "",
  ]);
  const csv = "\uFEFF" + "sep=,\n" + [header, ...body]
    .map((cols) => cols.map((col) => `"${String(col).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "generated-invoices.csv";
  link.click();
  URL.revokeObjectURL(url);
};

const exportSingleInvoiceExcel = (invoice: any, index: number) => {
  const isSale = invoice.type === "sale";
  const raw = invoice.rawTxn || {};

  const headers = [
    "Reference No",
    "Date",
    "Party Type",
    "Party Name",
    "Product",
    "Price",
    "Quantity",
    "Amount",
    "Discount",
    "Employee/Vendor",
    "Vehicle No",
    "Indent No",
    "Description",
  ];

  const refNo = getReferenceNumber(invoice, index);
  const dateStr = getInvoiceDate(invoice.date);
  const partyType = invoice.party_type || "";
  const partyName = invoice.party_name || "";
  
  const product = raw.product_name || raw.fuel_product || raw.productName || raw.fuelProduct || "";
  const price = raw.price_per_unit || raw.rate || raw.price || 0;
  const qty = raw.quantity || raw.qty || 0;
  const amount = raw.total_amount || raw.amount || raw.total || (Number(price) * Number(qty));
  const discount = raw.discount || 0;
  const employeeOrVendor = isSale 
    ? (raw.employee_name || raw.employee || "")
    : (raw.vendor_name || "");
  const vehicleNo = raw.vehicle_number || raw.vehicle_no || raw.vehicleNo || "";
  const indentNo = raw.indent_no || raw.indentNo || "";
  const description = raw.description || "";

  const row = [
    refNo,
    dateStr,
    partyType,
    partyName,
    product,
    price,
    qty,
    amount,
    discount,
    employeeOrVendor,
    vehicleNo,
    indentNo,
    description,
  ];

  // XML-HTML format that Microsoft Excel opens as a native sheet
  const excelHtml = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
    <head>
      <!--[if gte mso 9]>
      <xml>
        <x:ExcelWorkbook>
          <x:ExcelWorksheets>
            <x:ExcelWorksheet>
              <x:Name>Invoice Detail</x:Name>
              <x:WorksheetOptions>
                <x:DisplayGridlines/>
              </x:WorksheetOptions>
            </x:ExcelWorksheet>
          </x:ExcelWorksheets>
        </x:ExcelWorkbook>
      </xml>
      <![endif]-->
      <meta charset="utf-8">
    </head>
    <body>
      <table border="1">
        <thead>
          <tr style="background-color: #f2f2f2; font-weight: bold;">
            ${headers.map(h => `<th>${h}</th>`).join("")}
          </tr>
        </thead>
        <tbody>
          <tr>
            ${row.map(r => `<td>${String(r ?? "")}</td>`).join("")}
          </tr>
        </tbody>
      </table>
    </body>
    </html>
  `.trim();

  const blob = new Blob([excelHtml], { type: "application/vnd.ms-excel;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `invoice-${refNo}.xls`;
  link.click();
  URL.revokeObjectURL(url);
};

export default function GeneratedInvoices() {
  const { toast } = useToast();
  const [invoices, setInvoices] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(1);
  const [printModalInvoice, setPrintModalInvoice] = useState<any | null>(null);
  const [printModalIndex, setPrintModalIndex] = useState<number>(0);

  useEffect(() => {
    fetchInvoices();

    const interval = setInterval(fetchInvoices, 30000);
    window.addEventListener("focus", fetchInvoices);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", fetchInvoices);
    };
  }, []);

  const fetchInvoices = async () => {
    try {
      const vendorResponse = await fetch("/api/vendor-invoices", {
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      let vendorInvoices = [];
      if (vendorResponse.ok) {
        const vendorData = await vendorResponse.json();
        vendorInvoices = vendorData.rows || [];
      }

      const creditSalesResponse = await fetch("/api/credit-sales", {
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      let creditSalesInvoices = [];
      if (creditSalesResponse.ok) {
        const creditSalesData = await creditSalesResponse.json();
        creditSalesInvoices = creditSalesData.rows || [];
      }

      const allInvoices = [
        ...vendorInvoices.map((invoice: any) => ({
          id: `vendor-${invoice.id}`,
          reference_no: invoice.invoice_number,
          date: invoice.invoice_date || invoice.date || invoice.transaction_date,
          party_type: "Vendor",
          party_name: invoice.vendor_name || "Unknown",
          type: "purchase",
          rawTxn: invoice,
        })),
        ...creditSalesInvoices.map((sale: any) => ({
          id: `sale-${sale.id}`,
          reference_no: sale.bill_no || sale.invoice_no || "",
          date: sale.sale_date,
          party_type: "Credit Party",
          party_name: sale.organization_name || sale.organizationName || "Unknown Customer",
          type: "sale",
          rawTxn: sale,
        })),
      ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setInvoices(allInvoices);
    } catch (error) {
      console.error("Error in fetchInvoices:", error);
      setInvoices([]);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    await fetchInvoices();
    setPage(1);
    setLoading(false);
  };

  const resetFilters = () => {
    setFromDate("");
    setToDate("");
    setSearchQuery("");
    setPage(1);
  };

  const filteredInvoices = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;
    if (to) to.setHours(23, 59, 59, 999);

    return invoices.filter((invoice, index) => {
      const invoiceDate = invoice.date ? new Date(invoice.date) : null;
      if (from && invoiceDate && invoiceDate < from) return false;
      if (to && invoiceDate && invoiceDate > to) return false;

      if (!query) return true;
      const haystack = [
        getReferenceNumber(invoice, index),
        getInvoiceDate(invoice.date),
        invoice.party_type,
        invoice.party_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [fromDate, invoices, searchQuery, toDate]);

  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / 5));
  const safePage = Math.min(page, totalPages);
  const startIndex = filteredInvoices.length === 0 ? 0 : (safePage - 1) * 5;
  const visibleRows = filteredInvoices.slice(startIndex, startIndex + 5);
  const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((invoice) => selectedIds[invoice.id]);

  const toggleAllVisible = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = { ...prev };
      visibleRows.forEach((invoice) => {
        next[invoice.id] = checked;
      });
      return next;
    });
  };

  const deleteInvoice = async (invoice: any) => {
    const [source, rawId] = String(invoice.id).split("-");
    const endpoint = source === "vendor" ? `/api/vendor-invoices/${rawId}` : `/api/credit-sales/${rawId}`;

    const response = await fetch(endpoint, {
      method: "DELETE",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || "Unable to delete invoice");
    }

    setInvoices((prev) => prev.filter((row) => row.id !== invoice.id));
    setSelectedIds((prev) => {
      const next = { ...prev };
      delete next[invoice.id];
      return next;
    });
  };

  const handleDeleteOne = async (invoice: any) => {
    if (!window.confirm("Delete this invoice record?")) return;

    try {
      await deleteInvoice(invoice);
      toast({ title: "Deleted", description: "Invoice record deleted successfully." });
    } catch (error: any) {
      toast({
        title: "Delete failed",
        description: error.message || "Unable to delete invoice record.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteSelected = async () => {
    const rowsToDelete = invoices.filter((invoice) => selectedIds[invoice.id]);

    if (rowsToDelete.length === 0) {
      toast({ title: "No rows selected", description: "Select invoice rows before deleting." });
      return;
    }

    if (!window.confirm(`Delete ${rowsToDelete.length} selected invoice record(s)?`)) return;

    try {
      await Promise.all(rowsToDelete.map((invoice) => deleteInvoice(invoice)));
      toast({ title: "Deleted", description: "Selected invoice records deleted successfully." });
    } catch (error: any) {
      await fetchInvoices();
      toast({
        title: "Delete failed",
        description: error.message || "Some selected records could not be deleted.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4 bg-slate-50 min-h-full">
      <div className="h-11 -mx-6 -mt-6 px-6 flex items-center bg-gradient-to-r from-slate-100 to-white border-b border-slate-100">
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <Home className="h-4 w-4 text-slate-900" />
          <span className="font-bold text-slate-900">Dashboard</span>
          <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
          <span>Generated Invoice/Bills</span>
        </div>
      </div>

      <div className="rounded-md border border-slate-300 bg-white px-5 py-5">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[160px_1fr_140px_1fr_auto_auto] lg:items-center">
          <label className="text-xs font-bold text-slate-900">From Date</label>
          <Input
            type="date"
            value={toInputDate(fromDate)}
            onChange={(event) => setFromDate(event.target.value)}
            className="h-9 rounded-none border-slate-400 text-xs"
          />
          <label className="text-xs font-bold text-slate-900">To Date</label>
          <Input
            type="date"
            value={toInputDate(toDate)}
            onChange={(event) => setToDate(event.target.value)}
            className="h-9 rounded-none border-slate-400 text-xs"
          />
          <Button
            type="button"
            className="h-9 bg-green-600 px-4 text-xs font-bold hover:bg-green-700"
            onClick={handleSearch}
            disabled={loading}
          >
            <Search className="mr-1.5 h-3.5 w-3.5" />
            Search
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-9 rounded-none border-slate-900 px-4 text-xs font-bold"
            onClick={resetFilters}
          >
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Reset
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white px-4 py-4 shadow-lg">
        <Button
          type="button"
          variant="outline"
          className="h-8 border-red-500 px-2 text-xs font-bold text-red-600 hover:text-red-700"
          onClick={handleDeleteSelected}
        >
          <Trash2 className="mr-1 h-3.5 w-3.5" />
          Delete
        </Button>

        <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3 text-xs text-slate-900">
            <span>Show:</span>
            <select className="h-8 w-[72px] border border-slate-300 bg-white px-3 text-xs outline-none">
              <option>All</option>
            </select>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex items-center">
              <Button
                type="button"
                variant="outline"
                className="h-8 rounded-r-none border-green-500 text-green-600 hover:text-green-700 px-4 text-xs font-semibold"
                onClick={() => exportCsv(filteredInvoices)}
              >
                CSV
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-8 rounded-l-none border-red-500 text-red-600 hover:text-red-700 px-4 text-xs font-semibold"
                onClick={() => window.print()}
              >
                PDF
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-900">Filter:</span>
              <div className="relative">
                <Input
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                    setPage(1);
                  }}
                  placeholder="Type to filter..."
                  className="h-8 w-[220px] rounded-lg border-slate-300 pr-9 text-xs"
                />
                <Search className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[960px] table-fixed border-collapse text-xs">
            <colgroup>
              <col className="w-[6%]" />
              <col className="w-[8%]" />
              <col className="w-[16%]" />
              <col className="w-[16%]" />
              <col className="w-[18%]" />
              <col className="w-[25%]" />
              <col className="w-[11%]" />
            </colgroup>
            <thead>
              <tr className="border-y border-slate-300 text-slate-950">
                <th className="px-3 py-3 text-left">
                  <Checkbox
                    checked={allVisibleSelected}
                    onCheckedChange={(value) => toggleAllVisible(Boolean(value))}
                    className="h-3.5 w-3.5 rounded-none border-slate-400"
                  />
                </th>
                <th className="px-3 py-3 text-left font-bold">
                  <div className="flex items-center gap-1.5">
                    <span>Sl.No</span>
                    <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400 opacity-60" />
                  </div>
                </th>
                <th className="px-3 py-3 text-left font-bold">
                  <div className="flex items-center gap-1.5">
                    <span>Reference No.</span>
                    <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400 opacity-60" />
                  </div>
                </th>
                <th className="px-3 py-3 text-left font-bold">
                  <div className="flex items-center gap-1.5">
                    <span>Date</span>
                    <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400 opacity-60" />
                  </div>
                </th>
                <th className="px-3 py-3 text-left font-bold">
                  <div className="flex items-center gap-1.5">
                    <span>Party Type</span>
                    <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400 opacity-60" />
                  </div>
                </th>
                <th className="px-3 py-3 text-left font-bold">
                  <div className="flex items-center gap-1.5">
                    <span>Org/Cust</span>
                    <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400 opacity-60" />
                  </div>
                </th>
                <th className="px-3 py-3 text-center font-bold">
                  <div className="flex items-center justify-center gap-1.5">
                    <span>Action</span>
                    <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400 opacity-60" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((invoice, rowIndex) => (
                <tr key={invoice.id} className="border-b border-slate-200 odd:bg-slate-50 even:bg-white">
                  <td className="px-3 py-2.5">
                    <Checkbox
                      checked={Boolean(selectedIds[invoice.id])}
                      onCheckedChange={(value) =>
                        setSelectedIds((prev) => ({ ...prev, [invoice.id]: Boolean(value) }))
                      }
                      className="h-3.5 w-3.5 rounded-none border-slate-400"
                    />
                  </td>
                  <td className="px-3 py-2.5 text-slate-900">{startIndex + rowIndex + 1}</td>
                  <td className="px-3 py-2.5 font-medium text-blue-600">
                    <button
                      type="button"
                      onClick={() => {
                        setPrintModalInvoice(invoice);
                        setPrintModalIndex(startIndex + rowIndex);
                      }}
                      className="hover:underline text-left font-medium text-blue-600 focus:outline-none"
                    >
                      {getReferenceNumber(invoice, startIndex + rowIndex)}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-slate-900">{getInvoiceDate(invoice.date) || "-"}</td>
                  <td className="px-3 py-2.5 text-slate-900">{invoice.party_type || "-"}</td>
                  <td className="truncate px-3 py-2.5 text-slate-900">{invoice.party_name || "-"}</td>
                  <td className="px-3 py-2.5 text-center">
                    <button
                      type="button"
                      className="inline-flex text-blue-500 hover:text-blue-700 transition"
                      title="Delete invoice"
                      onClick={() => handleDeleteOne(invoice)}
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
              {visibleRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-10 text-center text-slate-500">
                    No invoices found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-900 px-1 py-3 text-xs text-slate-900 sm:flex-row sm:items-center sm:justify-between">
          <span>
            Showing {filteredInvoices.length === 0 ? 0 : startIndex + 1} to{" "}
            {Math.min(startIndex + visibleRows.length, filteredInvoices.length)} of {filteredInvoices.length} entries
          </span>
          <div className="flex items-center gap-4 self-end sm:self-auto">
            <button
              type="button"
              className="px-2 text-slate-500 disabled:opacity-30"
              disabled={safePage <= 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-8 border border-slate-400 bg-slate-100 px-3 py-2 text-center">{safePage}</span>
            <button
              type="button"
              className="px-2 text-slate-500 disabled:opacity-30"
              disabled={safePage >= totalPages}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <Dialog open={printModalInvoice !== null} onOpenChange={(open) => { if (!open) setPrintModalInvoice(null); }}>
        <DialogContent className="sm:max-w-[480px] rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
          <DialogHeader className="pb-4 border-b border-slate-100">
            <DialogTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <Printer className="h-5 w-5 text-indigo-600 animate-pulse" />
              Print Mode
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Select the format you want to print or export for Reference No.{" "}
              <span className="font-semibold text-slate-800">
                {printModalInvoice ? getReferenceNumber(printModalInvoice, printModalIndex) : ""}
              </span>
            </p>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Option 1: Print Bill */}
            <button
              onClick={() => {
                if (printModalInvoice) {
                  PrintService.printBill(printModalInvoice.rawTxn);
                  setPrintModalInvoice(null);
                }
              }}
              className="flex items-center gap-4 rounded-xl border border-slate-100 p-4 text-left transition-all duration-200 hover:border-indigo-100 hover:bg-indigo-50/50 hover:shadow-md group focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 transition-colors group-hover:bg-indigo-100 group-hover:text-indigo-700">
                <Receipt className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-slate-700 transition-colors group-hover:text-slate-900">
                  Print Bill
                </h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  Generate an 80mm thermal receipt roll layout
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-indigo-500" />
            </button>

            {/* Option 2: Print Invoice */}
            <button
              onClick={() => {
                if (printModalInvoice) {
                  PrintService.printInvoice(printModalInvoice.rawTxn);
                  setPrintModalInvoice(null);
                }
              }}
              className="flex items-center gap-4 rounded-xl border border-slate-100 p-4 text-left transition-all duration-200 hover:border-emerald-100 hover:bg-emerald-50/50 hover:shadow-md group focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 transition-colors group-hover:bg-emerald-100 group-hover:text-emerald-700">
                <FileText className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-slate-700 transition-colors group-hover:text-slate-900">
                  Print Invoice
                </h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  Generate a standard A4 sheet document layout
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-emerald-500" />
            </button>

            {/* Option 3: Print Excel */}
            <button
              onClick={() => {
                if (printModalInvoice) {
                  exportSingleInvoiceExcel(printModalInvoice, printModalIndex);
                  setPrintModalInvoice(null);
                }
              }}
              className="flex items-center gap-4 rounded-xl border border-slate-100 p-4 text-left transition-all duration-200 hover:border-amber-100 hover:bg-amber-50/50 hover:shadow-md group focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-50 text-amber-600 transition-colors group-hover:bg-amber-100 group-hover:text-amber-700">
                <FileSpreadsheet className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-slate-700 transition-colors group-hover:text-slate-900">
                  Print Excel
                </h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  Download spreadsheet in XLS format containing details
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-amber-500" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
